import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Globe2, ArrowRight, MapPin, Star } from 'lucide-react';
import { SPECIALTY_PAGES, MARKET_LINKS, SERVICE_AREAS_LINK } from '../../data/seoLinks';

export const MarketsDropdown = ({ compact = false }) => {
  const [open, setOpen] = useState(false);
  const t = useRef(null);
  const enter = () => { clearTimeout(t.current); setOpen(true); };
  const leave = () => { t.current = setTimeout(() => setOpen(false), 150); };

  return (
    <div className="relative" onMouseEnter={enter} onMouseLeave={leave}>
      <button type="button" onClick={() => setOpen((o) => !o)} className={`whitespace-nowrap flex items-center justify-center gap-1.5 font-semibold uppercase tracking-wider transition-colors ${compact ? 'w-full text-[11px] text-primary hover:text-secondary py-1.5' : 'nav-link-hover text-foreground/80 hover:text-primary text-sm px-3 py-3'}`} data-testid="nav-markets-dropdown">
        <Globe2 size={compact ? 13 : 16} /> Markets
        <ChevronDown size={16} className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} transition={{ duration: 0.15 }}
            className={`absolute top-full w-[780px] bg-white shadow-2xl border border-border grid grid-cols-2 z-50 ${compact ? 'right-0' : 'left-1/2 -translate-x-1/2'}`} data-testid="nav-markets-panel">
            <div className="bg-[#f8faf8] border-r border-border">
              <p className="bg-primary text-white px-4 py-2.5 text-xs font-bold uppercase tracking-widest">Countries We Serve</p>
              {MARKET_LINKS.map((m) => (
                <Link key={m.to} to={m.to} onClick={() => setOpen(false)} className="sub-link-hover flex items-start gap-3 px-4 py-3 hover:bg-primary/5 transition-colors group" data-testid={`nav-market-${m.key}`}>
                  <MapPin size={16} className="text-primary mt-0.5 shrink-0" />
                  <span><span className="block text-sm font-semibold group-hover:text-primary">{m.label}</span><span className="block text-[11px] leading-snug text-muted-foreground mt-0.5">{m.headline}</span></span>
                </Link>
              ))}
              <Link to="/service-areas" onClick={() => setOpen(false)} className="block px-4 py-3 group" data-testid="nav-market-service-areas">
                <span className="flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-primary group-hover:text-secondary">US Service Areas <ArrowRight size={12} /></span>
                <span className="block text-[11px] leading-snug text-muted-foreground mt-0.5">{SERVICE_AREAS_LINK.headline}</span>
              </Link>
            </div>
            <div>
              <p className="bg-secondary text-white px-4 py-2.5 text-xs font-bold uppercase tracking-widest">Specialty Services</p>
              {SPECIALTY_PAGES.map((p) => (
                <Link key={p.to} to={p.to} onClick={() => setOpen(false)} className="flex items-start gap-2 px-4 py-2 hover:bg-secondary/5 transition-colors group" data-testid={`nav-specialty-${p.to.slice(1)}`}>
                  <Star size={12} className="text-secondary shrink-0 mt-1" />
                  <span><span className="block text-sm font-semibold group-hover:text-secondary">{p.label}</span><span className="block text-[11px] leading-snug text-muted-foreground">{p.headline}</span></span>
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const MobileMarkets = () => {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button onClick={() => setOpen(!open)} className="flex items-center justify-between w-full py-2.5 text-foreground font-bold uppercase tracking-widest text-sm" data-testid="mobile-nav-markets">
        Markets <ChevronDown size={16} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="pl-4 space-y-1 mt-1">
          {[...MARKET_LINKS, ...SPECIALTY_PAGES].map((l) => (
            <Link key={l.to} to={l.to} className="block py-2 text-sm text-muted-foreground hover:text-primary" data-testid={`mobile-nav-markets-${l.to.slice(1)}`}>{l.label}</Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default MarketsDropdown;
